import { backgroundOrange, orange } from '@/theme/colors'
import { PlaceSuggestion } from '@components/LocationAutocomplete'
import Modal from '@components/Modal'
import { EventCategory } from '@orbit/api-types'
import React, { useEffect, useState } from 'react'
import IdeaForm from './IdeaForm'

interface AddIdeaModalProps {
	isOpen: boolean
	onClose: () => void
	onSubmit: (ideaData: {
		name: string
		description?: string
		category?: EventCategory
		location?: {
			fullText: string
			primaryText: string
			secondaryText?: string
			googlePlaceId?: string
			googleTypes: string[]
		}
		links?: string[]
	}) => void
	categoryFilter?: EventCategory
}

const AddIdeaModal: React.FC<AddIdeaModalProps> = ({ isOpen, onClose, onSubmit, categoryFilter }) => {
	const [name, setName] = useState('')
	const [description, setDescription] = useState('')
	const [category, setCategory] = useState<EventCategory | undefined>(categoryFilter)
	const [locationPrimaryText, setLocationPrimaryText] = useState('')
	const [selectedPlace, setSelectedPlace] = useState<PlaceSuggestion | null>(null)
	const [links, setLinks] = useState<string[]>([])

	// Update category when modal opens with a new categoryFilter
	useEffect(() => {
		if (isOpen) {
			setCategory(categoryFilter)
		}
	}, [isOpen, categoryFilter])

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (name.trim()) {
			// Filter out empty links
			const filteredLinks = links.filter(link => link.trim() !== '')

			onSubmit({
				name: name.trim(),
				description: description.trim() || undefined,
				category: category || undefined,
				location: selectedPlace ? {
					fullText: selectedPlace.fullText,
					primaryText: selectedPlace.primaryText,
					secondaryText: selectedPlace.secondaryText || undefined,
					googlePlaceId: selectedPlace.placeId,
					googleTypes: selectedPlace.types,
				} : locationPrimaryText.trim() ? {
					fullText: locationPrimaryText.trim(),
					primaryText: locationPrimaryText.trim(),
					googleTypes: [],
				} : undefined,
				links: filteredLinks.length > 0 ? filteredLinks : undefined,
			})
			// Reset form
			setName('')
			setDescription('')
			setCategory(categoryFilter)
			setLocationPrimaryText('')
			setSelectedPlace(null)
			setLinks([])
			onClose()
		}
	}

	const handleCancel = () => {
		setName('')
		setDescription('')
		setCategory(categoryFilter)
		setLocationPrimaryText('')
		setSelectedPlace(null)
		setLinks([])
		onClose()
	}

	const handleLocationSelect = (place: PlaceSuggestion) => {
		setSelectedPlace(place)

		// When location is set, attempt to prefill name as well
		// Get the old location name from selectedPlace or locationPrimaryText
		const oldLocationName = (selectedPlace?.primaryText ?? selectedPlace?.fullText ?? locationPrimaryText ?? '')
			.substring(0, 75)
			.trim()

		// If name is empty or matches the old location name, update it with the new location
		const trimmedName = name.trim()
		if (trimmedName === '' || trimmedName === oldLocationName) {
			// Max length on name is 75 (same as iOS)
			const newName = (place.primaryText ?? place.fullText)
				.substring(0, 75)
				.trim()
			setName(newName)
		}
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleCancel}
			title="Add Idea"
			closeOnBackdropClick={false}
			backgroundColor={backgroundOrange}
			closeButtonColor={orange}
		>
		<IdeaForm
			name={name}
			description={description}
			category={category}
			locationPrimaryText={locationPrimaryText}
			locationSecondaryText={selectedPlace?.secondaryText || undefined}
			links={links}
				onNameChange={setName}
				onDescriptionChange={setDescription}
				onCategoryChange={setCategory}
				onLocationPrimaryTextChange={setLocationPrimaryText}
				onLocationSelect={handleLocationSelect}
				onLinksChange={setLinks}
				onSubmit={handleSubmit}
				onCancel={handleCancel}
				submitButtonText="Add Idea"
				submitDisabled={!name.trim()}
			/>
		</Modal>
	)
}

export default AddIdeaModal
